import React from 'react';

// リンク検出は3系統：メール（=リンク化しない）／URL（http(s)/www）／生ドメイン（https補完）。
// ラベルはASCIIのみ（日本語散文の誤爆防止）、TLDはホワイトリスト（app.js/config.json等のファイル名を除外）。
const LINK_TLDS =
  'com|net|org|edu|gov|info|biz|io|co|me|tv|app|dev|ai|xyz|shop|site|online|store|work|studio|blog|page|link|live|media|world|group|inc|ltd|club|fun|life|design|art|band|fm|jp|us|uk|ca|au|de|fr|it|es|nl|se|no|fi|dk|ch|at|be|eu|kr|cn|tw|hk|sg|th|in|id|ph|vn|my|br|mx|ru|pl|nz';
const EMAIL_SRC = "[A-Za-z0-9._%+\\-]+@[A-Za-z0-9.\\-]+\\.[A-Za-z]{2,}";
const URL_SRC = "(?:https?:\\/\\/|www\\.)[^\\s<>\"']+";
const BARE_DOMAIN_SRC = `(?:[A-Za-z0-9](?:[A-Za-z0-9\\-]*[A-Za-z0-9])?\\.)+(?:${LINK_TLDS})(?![A-Za-z0-9\\-])(?:[\\/?#][^\\s<>"']*)?`;
const URL_DETECT_RE = new RegExp(`(${EMAIL_SRC})|(${URL_SRC})|(${BARE_DOMAIN_SRC})`, 'gi');
const CLOSING_TO_OPENING_BRACKET = {
  ')': '(',
  ']': '[',
  '}': '{',
  '）': '（',
  '］': '［',
  '｝': '｛',
};
const OPENING_BRACKETS = new Set(Object.values(CLOSING_TO_OPENING_BRACKET));

const hasUnmatchedClosingBracket = (value, closingBracket) => {
  const openingBracket = CLOSING_TO_OPENING_BRACKET[closingBracket];
  if (!openingBracket) return false;
  let depth = 0;
  for (const char of String(value || '')) {
    if (char === openingBracket) depth += 1;
    if (char === closingBracket) {
      if (depth === 0) return true;
      depth -= 1;
    }
  }
  return false;
};

const trimUrlTrailingPunctuation = (value) => {
  let url = String(value || '');
  let suffix = '';
  while (url.length > 0) {
    const lastChar = Array.from(url).pop();
    if (/[.,!?、。]/.test(lastChar)) {
      suffix = `${lastChar}${suffix}`;
      url = url.slice(0, -lastChar.length);
      continue;
    }
    if (CLOSING_TO_OPENING_BRACKET[lastChar] && hasUnmatchedClosingBracket(url, lastChar)) {
      suffix = `${lastChar}${suffix}`;
      url = url.slice(0, -lastChar.length);
      continue;
    }
    if (OPENING_BRACKETS.has(lastChar)) {
      suffix = `${lastChar}${suffix}`;
      url = url.slice(0, -lastChar.length);
      continue;
    }
    break;
  }
  return { url, suffix };
};

export const linkifyTextNodes = (text, { linkClassName = 'im-autolink', transformUrl = null } = {}) => {
  if (!text) return '';

  const nodes = [];
  let lastIndex = 0;
  const source = String(text);

  source.replace(URL_DETECT_RE, (match, emailMatch, urlMatch, bareMatch, offset) => {
    if (lastIndex < offset) {
      nodes.push(<React.Fragment key={`t-${lastIndex}`}>{source.slice(lastIndex, offset)}</React.Fragment>);
    }

    // メールアドレスはリンク化しない（プレーンテキストのまま残す）。
    if (emailMatch) {
      nodes.push(<React.Fragment key={`e-${offset}`}>{match}</React.Fragment>);
      lastIndex = offset + match.length;
      return match;
    }

    const { url: cleanUrl, suffix } = trimUrlTrailingPunctuation(urlMatch || bareMatch);
    const defaultHref =
      cleanUrl.startsWith('http://') || cleanUrl.startsWith('https://')
        ? cleanUrl
        : `https://${cleanUrl}`;
    const transformedHref =
      typeof transformUrl === 'function'
        ? String(transformUrl(defaultHref) || defaultHref)
        : defaultHref;
    const displayUrl = transformedHref !== defaultHref ? transformedHref : cleanUrl;

    nodes.push(
      <a
        key={`a-${offset}`}
        href={transformedHref}
        target="_blank"
        rel="noopener noreferrer"
        className={linkClassName}
        title={displayUrl}
      >
        {displayUrl}
      </a>
    );
    if (suffix) {
      nodes.push(<React.Fragment key={`t-suffix-${offset}`}>{suffix}</React.Fragment>);
    }

    lastIndex = offset + match.length;
    return match;
  });

  if (lastIndex < source.length) {
    nodes.push(<React.Fragment key={`t-${lastIndex}`}>{source.slice(lastIndex)}</React.Fragment>);
  }

  return nodes;
};
